import { writeStorage } from "@rehooks/local-storage";
import { useFormik } from "formik";
import React, { useState } from "react";
import { Alert, Col, Container, Form, Row } from "react-bootstrap";
import Button from "react-bootstrap-button-loader";
import toast from "react-hot-toast";
import { BsChevronLeft, BsInfoCircle } from "react-icons/bs";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import { OtpModal } from "../Components/Modals/OtpModal";
import { Sidebar } from "../Components/Sidebar";
import { useAuth } from "../contexts/auth";
import axiosInstance from "../utils/axiosConfig";

// Validation schema for email update
const emailValidationSchema = Yup.object().shape({
  email: Yup.string().required("Email is Required").email("Enter Valid Email"),
});

// Validation schema for mobile update
const mobileValidationSchema = Yup.object().shape({
  mobileNumber: Yup.string()
    .trim()
    .matches("^((?!([0-5]))[0-9]{10})+$", "Enter Valid Mobile Number")
    .required("Mobile Number is required")
    .min(10, "Mobile number must be at least 10 digits")
    .max(10, "Mobile number must be at least 10 digits"),
});

export const ManageProfile = () => {
  const { user, onUpdateUser } = useAuth();
  const [editType, setEditType] = useState("");
  const [loader, setLoader] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [referenceId, setReferenceId] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Initialize form with existing user data
  const emailForm = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: emailValidationSchema,
    onSubmit: (values) => {
      setLoader(true);
      sendOtp();
    },
  });

  const mobileForm = useFormik({
    initialValues: {
      mobileNumber: "",
    },
    validationSchema: mobileValidationSchema,
    onSubmit: (values) => {
      setLoader(true);
      sendOtp();
    },
  });

  const sendOtp = () => {
    const otpType = editType === "email" ? "email_otp" : "mobile_otp";
    const otpData = {
      otpType: otpType,
      flow: "update",
      loginContext: "profile",
      requestId:
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15),
    };

    if (editType === "email") {
      otpData.emailId = emailForm.values.email;
      // For email updates, we don't need to include the mobile number
    } else {
      otpData.mobileNo = mobileForm.values.mobileNumber;
      // For mobile updates, we don't need to include the email
    }

    axiosInstance()
      .post("auth/generate-otp", otpData)
      .then((res) => {
        if ([200, 1136].includes(res.data.code)) {
          setShowOtpModal(true);
          setLoader(false);
          setReferenceId(res.data.referenceId);
          toast.success("OTP sent successfully");
        } else {
          toast.error(res.data.message);
          setLoader(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoader(false);
        toast.error(err.toString());
      });
  };

  const updateProfile = (otp) => {
    setLoader(true);

    const updateData = {
      referenceId: referenceId,
      userId: user?.corporateUserId,
      panNumber: user?.panNumber,
    };

    if (editType === "email") {
      updateData.newEmailId = emailForm.values.email;
      updateData.currentEmailId = user?.emailId;
      updateData.emailIdOtp = otp;
    } else {
      updateData.newMobileNo = mobileForm.values.mobileNumber;
      updateData.currentMobileNo = user?.mobileNo;
      updateData.mobileOtp = otp;
    }

    axiosInstance()
      .post("consent-api/updateCorporateUserProfile", updateData)
      .then((res) => {
        if (res.data.code === 200) {
          setLoader(false);
          setShowOtpModal(false);

          // Update local storage with new user data
          const updatedUser = {
            ...user,
            ...(editType === "email"
              ? { emailId: emailForm.values.email }
              : { mobileNo: mobileForm.values.mobileNumber }),
          };

          writeStorage("user", JSON.stringify(updatedUser));

          // // Store the new access token if provided
          // if (res.data.data?.accessToken) {
          //   localStorage.setItem("token", res.data.data.accessToken);
          //   localStorage.setItem("access_token", res.data.data.accessToken);
          // }

          onUpdateUser(updatedUser);
          
          // Show success message
          setSuccessMessage(
            `Your ${
              editType === "email" ? "email" : "mobile number"
            } has been updated successfully.`
          );

          // Reset form
          if (editType === "email") {
            emailForm.resetForm();
          } else {
            mobileForm.resetForm();
          }

          setEditType("");
          
          // // Update the auth context
          // onLogin(window.location.pathname, updatedUser);
          
          toast.success(`${editType === "email" ? "Email" : "Mobile number"} updated successfully`);
        } else {
          toast.error(res.data.message);
          setLoader(false);
        }
      })
      .catch((err) => {
        console.log(err);
        setLoader(false);
        toast.error(err.toString());
      });
  };

  return (
    <Container fluid className="px-4 py-4">
      <Row>
        <Link to={user?.hasConsents ? '/home' : '/new-consent'} className='fw-bold mb-4 d-inline-block text-decoration-none'>
            <BsChevronLeft className='me-1' />Back
        </Link>
        <Col md={3}>
          <Sidebar gradientBg={true}/>
        </Col>
        <Col md={7} className="offset-1">
          <div className="form-head mb-4">
            <h2 className="fw-bold mb-2">Manage Profile</h2>
            <p className="opacity-75 mb-0">
              Update your email or mobile number
            </p>
          </div>

          {successMessage && (
            <Alert
              variant="success"
              onClose={() => setSuccessMessage("")}
              dismissible
            >
              {successMessage}
            </Alert>
          )}
          {/* Conditional rendering for profile info */}
          <div className="profile-info mb-4 p-3 border rounded">
            <h4>Current Profile Information</h4>
             <p><strong>Email:</strong> {user?.emailId || "Not available"}</p>
            {user?.mobileNo && (
              <p><strong>Mobile Number:</strong> {user.mobileNo}</p>
            )}
            {user?.panNumber && (
              <p><strong>PAN Number:</strong> {user.panNumber}</p>
            )}
          </div>

          <div className="edit-options mb-4">
            <h4>What would you like to update?</h4>
            <Form.Check
              type="radio"
              id="edit-email"
              name="editType"
              label="Update Email Address"
              onChange={() => setEditType("email")}
              checked={editType === "email"}
              className="mb-2"
            />
            {/* Conditionally render the mobile update option */}
            {user?.mobileNo ? (
              <Form.Check
                type="radio"
                id="edit-mobile"
                name="editType"
                label="Update Mobile Number"
                onChange={() => setEditType("mobile")}
                checked={editType === "mobile"}
                className="mb-2"
              />
            ) : (
                <Alert variant="info" className="mt-3">
                    To add or update your Mobile Number, please create your first consent.
                </Alert>
            )}
          </div>

          {editType === "email" && (
            <Form noValidate onSubmit={emailForm.handleSubmit}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="emailId">
                    <Form.Label className="d-flex align-items-center">
                      New Email ID
                      <Button
                        variant="link p-0 m-0 ms-3 lh-1 text-reset"
                        title="Enter your new email address"
                      >
                        <BsInfoCircle className="fs-6" />
                      </Button>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      placeholder="eg. john.doe@gmail.com"
                      onChange={emailForm.handleChange}
                      value={emailForm.values.email}
                      onBlur={emailForm.handleBlur}
                      isInvalid={
                        emailForm.touched.email && !!emailForm.errors.email
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {emailForm.errors.email}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      An OTP will be sent to this email for verification
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Button
                type="submit"
                className="btn btn-primary mt-2"
                loading={loader}
                disabled={
                  !emailForm.values.email ||
                  emailForm.values.email === user?.emailId
                }
              >
                Update Email
              </Button>
            </Form>
          )}

          {editType === "mobile" && (
            <Form noValidate onSubmit={mobileForm.handleSubmit}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3" controlId="mobileNumber">
                    <Form.Label className="d-flex align-items-center">
                      New Mobile Number
                      <Button
                        variant="link p-0 m-0 ms-3 lh-1 text-reset"
                        title="Enter your new mobile number"
                      >
                        <BsInfoCircle className="fs-6" />
                      </Button>
                    </Form.Label>
                    <Form.Control
                      type="tel"
                      name="mobileNumber"
                      placeholder="eg. 9876543210"
                      onChange={mobileForm.handleChange}
                      value={mobileForm.values.mobileNumber}
                      onBlur={mobileForm.handleBlur}
                      isInvalid={
                        mobileForm.touched.mobileNumber &&
                        !!mobileForm.errors.mobileNumber
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {mobileForm.errors.mobileNumber}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      An OTP will be sent to this number for verification
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Button
                type="submit"
                className="btn btn-primary mt-2"
                loading={loader}
                disabled={
                  !mobileForm.values.mobileNumber ||
                  mobileForm.values.mobileNumber === user?.mobileNo
                }
              >
                Update Mobile Number
              </Button>
            </Form>
          )}
        </Col>
      </Row>

      {/* OTP Modal */}
      <OtpModal
        showOtpModal={showOtpModal}
        setShowOtpModal={setShowOtpModal}
        loginSubmit={updateProfile}
        resendOtp={sendOtp}
        loader={loader}
        otpContext={editType} // 'email' or 'mobile' depending on what's being updated
      />
    </Container>
  );
};
